import { useEffect, useState } from "react";
import { useRouter } from "next/router";
import classNames from "classnames";

import Checkbox from "components/checkbox/checkbox";
import { WarningIcon } from "components/icons/icons";
import Button from "components/buttonNew/button";
import { StaticTag } from "components/staticTag/staticTag";
import Spinner from "components/spinner/spinner";

import { BreakfastShort } from "content-ui/breakfast/types";
import { sendBreakfastRsvp } from "content-ui/breakfast/utils";
import { trackGTMEvent } from "lib/utils";
import { useMixpanelTracking } from "lib/hooks/useMixpanelTracking";
import { useUserData } from "lib/store/user";

export type ApplyBlockProps = BreakfastShort & {
  component: "UpcomingEvent" | "DetailHero";
  hasRsvped: (id: string) => boolean;
  isRsvpReady: boolean;
};

const ApplyBlock = ({
  id,
  title,
  applicationPolicy,
  agreePolicyApplicationErrorMessage,
  applyButtonText,
  applicationsStatus,
  component,
  successfulSubmitMessage,
  hasRsvped,
  isRsvpReady,
  applicationsInReviewNotice,
}: ApplyBlockProps) => {
  const router = useRouter();
  const { isAuthorisedUser, userId } = useUserData();
  const { track } = useMixpanelTracking();
  const [checked, setChecked] = useState(false);
  const [isError, setIsError] = useState(false);
  const [rsvped, setRsvped] = useState<null | boolean>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isRsvpReady) {
      setRsvped(hasRsvped(id));
    }
  }, [hasRsvped, id, isRsvpReady]);

  const handleCheckedChange = (checked: boolean) => {
    setIsError(false);

    setChecked(checked);
  };

  const handleApply = async () => {
    if (!checked) {
      setIsError(true);
    } else {
      if (!isAuthorisedUser) {
        const utm_source = (router.query["utm_source"] as string) || "null";
        const utm_medium = (router.query["utm_medium"] as string) || "null";
        const utm_campaign = (router.query["utm_campaign"] as string) || "null";
        const utm_content = (router.query["utm_content"] as string) || "null";

        const params = new URLSearchParams({
          breakfastId: id,
          marketingConsent: String(checked),
          utm_source,
          utm_medium,
          utm_campaign,
          utm_content,
          redirectUrl: window.location.href,
        });

        window.location.assign(`/members/login?${params}`);
      } else {
        trackGTMEvent({
          event: "breakfast join",
          eventId: id,
          eventName: title,
          userId: userId || null,
        });

        await track("Breakfast apply", {
          Component: component,
          "Breakfast id": id,
          "Breakfast title": title,
          "Click text": applyButtonText,
        });

        setIsLoading(true);

        const result = await sendBreakfastRsvp({ fields: { breakfastId: id } });

        setIsLoading(false);

        if (result.success && result.breakfastIdAddedToPiano) {
          setRsvped(true);
        }
      }
    }
  };

  const customDashedClasses = classNames(
    "h-[1px] lg:hidden",
    "[background-size:_6px_2px]",
    "[background-image:_linear-gradient(to_right,_#8E8E99,_#8E8E99_2px,_transparent_2px,_transparent_6px)]"
  );

  if (!isRsvpReady || rsvped === null) {
    return null;
  }

  if (rsvped) {
    return (
      <div className={"flex flex-row gap-[30px]"}>
        <StaticTag text={successfulSubmitMessage} noWrap={false} />
      </div>
    );
  }

  if (applicationsStatus === "closed") {
    return (
      <div
        className={classNames("flex flex-col gap-3 lg:gap-2", {
          "mt-[20px]": component === "DetailHero",
        })}
      >
        <StaticTag
          text={"Applications in review"}
          color={"grey"}
          noWrap={false}
        />
        {applicationsInReviewNotice && component === "DetailHero" && (
          <p
            className={"text-[16px] leading-[130%] text-mono-40 lg:text-[14px]"}
          >
            {applicationsInReviewNotice}
          </p>
        )}
      </div>
    );
  }

  return (
    <>
      {component === "UpcomingEvent" && (
        <div
          className={classNames("mb-5 mt-2", customDashedClasses)}
          data-testid={"applyBlock__separator"}
        ></div>
      )}
      <div
        className={classNames("flex flex-row gap-x-[30px] gap-y-[20px]", {
          "flex-col items-center lg:flex-row": component === "UpcomingEvent",
          "flex-col items-center md:flex-row": component === "DetailHero",
        })}
      >
        <div className={"inline-flex gap-[10px]"}>
          <div className={"mt-[3px]"}>
            <Checkbox
              id={`application-${id}`}
              name={`application-${id}`}
              checked={checked}
              data-testid={"consent"}
              onCheckedChange={handleCheckedChange}
            />
          </div>
          <div className={"flex flex-col gap-2"}>
            <label
              htmlFor={`application-${id}`}
              className={
                "cursor-pointer text-[14px] leading-[130%] text-mono-40"
              }
            >
              {applicationPolicy}
            </label>
            {isError && (
              <p
                className={"inline-flex items-center text-[#E52900]"}
                id={`error_${id}`}
                data-testid={"detailHero__errorMessage"}
              >
                <span className={"mr-[4px] flex items-center"}>
                  <WarningIcon />
                </span>
                <span className={"text-[14px] leading-[130%]"}>
                  {agreePolicyApplicationErrorMessage}
                </span>
              </p>
            )}
          </div>
        </div>
        <div className={"flex-none"}>
          <Button
            size={"medium"}
            onClick={handleApply}
            disabled={isLoading}
            data-testid={"apply-cta"}
          >
            <span className={"relative"}>
              <span>
                {isLoading && (
                  <span
                    data-testid={"applyBlock__loading"}
                    className={
                      "absolute left-[calc(50%_-_12px)] right-auto top-0"
                    }
                  >
                    <Spinner colour={"white"} width={24} height={24} />
                  </span>
                )}
                <span className={classNames(isLoading && "opacity-0")}>
                  {isAuthorisedUser ? applyButtonText : "Register to apply"}
                </span>
              </span>
            </span>
          </Button>
        </div>
      </div>
    </>
  );
};

export default ApplyBlock;
